import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { QuotationStateEnums } from 'src/app/enum/quotation-enums';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { OrderModel } from 'src/app/models/preliminaries/order.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { BreakdownByDetailTableModel } from 'src/app/models/tables/preliminaries/breakdown-by-detail-table.model';
import { QuotationDetailSelectionTableModel } from 'src/app/models/tables/preliminaries/quotation-detail-selection-table.model';
import { QuotationSelectionTableModel } from 'src/app/models/tables/preliminaries/quotation-selection-table.model';
import { BreakdownService } from 'src/app/services/preliminaries/breakdown.service';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { QuotationDetailService } from 'src/app/services/preliminaries/quotation-detail.service';
import { QuotationService } from 'src/app/services/preliminaries/quotation.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-order-from-quotation-stepper',
  templateUrl: './order-from-quotation-stepper.component.html',
  styleUrls: ['./order-from-quotation-stepper.component.scss'],
  standalone: false
})
export class OrderFromQuotationStepperComponent implements OnInit {

  private readonly breakdownService = inject(BreakdownService);
  public readonly config = inject(DynamicDialogConfig);
  private readonly orderDetailService = inject(OrderDetailService);
  private readonly quotationDetailService = inject(QuotationDetailService);
  private readonly quotationService = inject(QuotationService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly ref = inject(DynamicDialogRef, { optional: true });

  ascendant: boolean = true;
  breakdownHeader: ColumnResponseModel[] = [];
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Agregar',
      icon: 'pi pi-plus-circle',
      color: ActionColor.SUCCESS,
      action: 'add'
    },
  ];
  columnSort: string | null = null;
  currentStep: number = 1;
  dataBreakdown: PagedResponseModel<BreakdownByDetailTableModel> =
    new PagedResponseModel<BreakdownByDetailTableModel>();
  dataQuotation: PagedResponseModel<QuotationSelectionTableModel> =
    new PagedResponseModel<QuotationSelectionTableModel>();
  dataQuotationDetail: PagedResponseModel<QuotationDetailSelectionTableModel> =
    new PagedResponseModel<QuotationDetailSelectionTableModel>();
  filtersBreakdown: ColumnResponseModel[] = [];
  filtersQuotation: ColumnResponseModel[] = [];
  filtersQuotationDetail: ColumnResponseModel[] = [];
  limit: number = 100;
  loading: boolean = false;
  orderModel: OrderModel = new OrderModel();
  page: number = 1;
  quotationDetailHeader: ColumnResponseModel[] = [];
  quotationDetailId: number | null = null;
  quotationHeader: ColumnResponseModel[] = [];
  quotationId: number | null = null;
  suscription: Subscription;
  readonly stepPanel = StepPanelEnums;
  swMandatoryQuotation: boolean = false;
  swShowOnlyOrderBreakdown: boolean = false;

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.orderModel = inputs.orderModel;
      this.swMandatoryQuotation = inputs.swMandatoryQuotation;
      this.swShowOnlyOrderBreakdown = inputs.swShowOnlyOrderBreakdown;
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  breakdownRowClass = (item: BreakdownByDetailTableModel): string =>
    this.orderModel.orderId !== 0 && item.costCenterId === this.orderModel.costCenterId ? 'highlight-row' : '';

  private clearFutureStepSelections(currentStep: number) {
    if (this.swShowOnlyOrderBreakdown) {
      switch (currentStep) {
        case StepPanelEnums.PanelOne:
          this.quotationId = null;
          break;
      }
    } else {
      switch (currentStep) {
        case StepPanelEnums.PanelOne:
          this.quotationId = null;
          break;
        case StepPanelEnums.PanelTwo:
          this.quotationDetailId = null;
          break;
      }
    }
  }

  close(reload: 'header' | 'detail') {
    const data: ModalObjectType = {};
    if (reload === 'header') {
      data.params = { orderId: this.orderModel.orderId };
    } else {
      data.params = { reloadDetail: true };
    }
    this.ref?.close(data);
  }

  async createOrderOrDetail(selectedItems: number[]) {
    try {
      let reload: 'header' | 'detail' = this.orderModel.orderId != 0 ? 'detail' : 'header';
      const observationCommercial = this.orderModel.observationCommercial;
      this.orderModel = await firstValueFrom(
        this.orderDetailService.createOrderByQuotation(selectedItems, this.orderModel.orderId)
      );
      //Si el pedido heredó la observación comercial de la cotización, recargo también el encabezado:
      if (this.orderModel.observationCommercial !== observationCommercial) reload = 'header';
      this.toastHandlerService.showToast(
        'success',
        this.swMandatoryQuotation
          ? 'El pedido fue creado exitosamente'
          : 'El detalle del pedido fue creado exitosamente.'
      );
      this.close(reload);
    } catch (error) {
      console.error('Error al crear el pedido.', error);
    }
  }

  async getBreakdownForSelection() {
    try {
      this.dataBreakdown = await firstValueFrom(
        this.breakdownService.getBreakdownByDetailId(
          this.page, this.limit, this.columnSort, this.ascendant, this.filtersBreakdown,
          this.quotationDetailId ?? 0, this.quotationId ?? 0, this.orderModel.orderId
        )
      );
    } catch (error) {
      this.dataBreakdown = new PagedResponseModel<BreakdownByDetailTableModel>();
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getColumnsForBreakdown() {
    try {
      this.breakdownHeader = await firstValueFrom(
        this.breakdownService.getColumnsBreakdownByDetailId()
      );
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForQuotation() {
    try {
      this.quotationHeader = await firstValueFrom(
        this.quotationService.getColumnsForQuotationSelection()
      );
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForQuotationDetail() {
    try {
      this.quotationDetailHeader = await firstValueFrom(
        this.quotationDetailService.GetColumnsForQuotationDetailsSelection()
      );
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      let actions: Record<number, () => Promise<any>>;

      if (this.swShowOnlyOrderBreakdown) {
        actions = {
          [StepPanelEnums.PanelOne]: () => {
            this.filtersQuotation = [];
            return Promise.all([
              this.getColumnsForQuotation(),
              this.getQuotationsForSelection()
            ]);
          },
          [StepPanelEnums.PanelTwo]: () => {
            if (!this.quotationId) {
              return Promise.resolve();
            }
            this.filtersBreakdown = [];
            return Promise.all([
              this.getColumnsForBreakdown(),
              this.getBreakdownForSelection()
            ]);
          }
        };
      } else {
        actions = {
          [StepPanelEnums.PanelOne]: () => {
            this.filtersQuotation = [];
            return Promise.all([
              this.getColumnsForQuotation(),
              this.getQuotationsForSelection()
            ]);
          },
          [StepPanelEnums.PanelTwo]: () => {
            if (!this.quotationId) {
              return Promise.resolve();
            }
            this.filtersQuotationDetail = [];
            return Promise.all([
              this.getColumnsForQuotationDetail(),
              this.getQuotationDetailsForSelection()
            ]);
          },
          [StepPanelEnums.PanelThree]: () => {
            if (!this.quotationDetailId) {
              return Promise.resolve();
            }
            this.filtersBreakdown = [];
            return Promise.all([
              this.getColumnsForBreakdown(),
              this.getBreakdownForSelection()
            ]);
          }
        };
      }

      if (actions[step]) await actions[step]();
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getQuotationById(quotationId: number) {
    try {
      const quotation = await firstValueFrom(
        this.quotationService.getQuotationById(quotationId)
      );
      const restriction = this.getQuotationRestriction(quotation);
      if (restriction) {
        this.quotationId = null;
        this.toastHandlerService.showToast('warning', restriction);
        return;
      }
      this.quotationId = quotation.quotationId;
    } catch (error) {
      console.error('Error al obtener la cotización.', error);
    }
  }

  async getQuotationDetailById(quotationDetailId: number) {
    try {
      const quotationDetail = await firstValueFrom(
        this.quotationDetailService.getQuotationDetailById(quotationDetailId)
      );
      if (this.swSolutionCostCenterRule && quotationDetail.costCenterId != this.orderModel.costCenterId) {
        this.quotationDetailId = null;
        this.toastHandlerService.showToast(
          'warning',
          'La solución seleccionada no pertenece al centro de costos del pedido.',
        );
        return;
      }
      this.quotationDetailId = quotationDetail.quotationDetailId;
    } catch (error) {
      console.error('Error al obtener detalle de cotización.', error);
    }
  }

  async getQuotationsForSelection() {
    try {
      this.dataQuotation = await firstValueFrom(
        this.quotationService.getQuotationsForSelection(
          this.page, this.limit, this.columnSort, this.ascendant, this.filtersQuotation, false
        )
      );
    } catch (error) {
      console.error('Error al obtener la lista de los artículos.', error);
    }
  }

  async getQuotationDetailsForSelection() {
    try {
      this.dataQuotationDetail = await firstValueFrom(
        this.quotationDetailService.getQuotationDetailsForSelection(
          this.page, this.limit, this.columnSort, this.ascendant, this.filtersQuotationDetail, this.quotationId ?? 0
        )
      );
    } catch (error) {
      this.dataQuotationDetail = new PagedResponseModel<QuotationDetailSelectionTableModel>();
      console.error('Error al obtener los detalles de cotización.', error);
    }
  }

  getQuotationRestriction(quotation: { quotationId: number, clientId?: number | null, quotationTypeId: number, quotationStateId: number }): string | null {
    const isExistingOrder = this.orderModel.orderId != 0;
    if (isExistingOrder && this.orderModel.quotationId != null && this.orderModel.quotationId !== quotation.quotationId)
      return 'No se permite agregar detalles de una cotización diferente a la del encabezado.';
    if (quotation.quotationStateId != QuotationStateEnums.approved)
      return 'La cotización seleccionada debe estar aprobada para poder continuar.';
    if (isExistingOrder && quotation.quotationTypeId != this.orderModel.orderTypeId)
      return 'La cotización seleccionada debe ser del mismo tipo del pedido.';
    if (isExistingOrder && quotation.clientId != this.orderModel?.costCenter?.clientId)
      return 'La cotización seleccionada pertenece a un cliente distinto al del pedido.';
    return null;
  }

  async goToStep(step: number, activateCallback: any) {
    if (step < this.currentStep) this.clearFutureStepSelections(step);
    if (!this.validateCurrentStepSelection(step)) return;
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  async onAction(event: { action: string; item?: BreakdownByDetailTableModel; selectedItems?: number[] }) {
    const { action, selectedItems } = event;
    if (action !== 'add' || !selectedItems || selectedItems.length === 0) return;
    if (this.swShowOnlyOrderBreakdown) {
      const { orderId, costCenterId } = this.orderModel;
      const selectedIdsSet = new Set(selectedItems);
      const selectedItemsData = this.dataBreakdown.items.filter(item => selectedIdsSet.has(item.id));
      if (orderId !== 0) {
        const hasDifferentCostCenter = selectedItemsData.some(item => item.costCenterId !== costCenterId);
        if (hasDifferentCostCenter) {
          this.toastHandlerService.showToast(
            'warning',
            'Todos los artículos seleccionados deben pertenecer al mismo centro de costos del pedido.'
          );
          return;
        }
      }
      if (orderId === 0) {
        const firstCostCenter = selectedItemsData[0]?.costCenterId;
        const hasMultipleCostCenters = selectedItemsData.some(item => item.costCenterId !== firstCostCenter);
        if (hasMultipleCostCenters) {
          this.toastHandlerService.showToast(
            'warning',
            'Todos los artículos seleccionados deben pertenecer al mismo centro de costos.'
          );
          return;
        }
      }
    }
    await this.createOrderOrDetail(selectedItems);
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);

    const cleanFilters = this.removeDuplicates(filters);

    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filtersQuotation = cleanFilters;
        this.filtersQuotation.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        if (this.swShowOnlyOrderBreakdown) {
          this.filtersBreakdown = cleanFilters;
          this.filtersBreakdown.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        } else {
          this.filtersQuotationDetail = cleanFilters;
          this.filtersQuotationDetail.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        }
        break;
      }
      case StepPanelEnums.PanelThree: {
        this.filtersBreakdown = cleanFilters;
        this.filtersBreakdown.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelThree));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;

    if (this.swShowOnlyOrderBreakdown) {
      switch (this.currentStep) {
        case StepPanelEnums.PanelOne:
          await this.getQuotationsForSelection();
          break;
        case StepPanelEnums.PanelTwo:
          await this.getBreakdownForSelection();
          break;
      }
    } else {
      switch (this.currentStep) {
        case StepPanelEnums.PanelOne:
          await this.getQuotationsForSelection();
          break;
        case StepPanelEnums.PanelTwo:
          await this.getQuotationDetailsForSelection();
          break;
        case StepPanelEnums.PanelThree:
          await this.getBreakdownForSelection();
          break;
      }
    }
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0;
    if (isInvalid) {
      if (step === StepPanelEnums.PanelOne) {
        this.filtersQuotation = this.filtersQuotation.filter(f => f.propertyName !== propertyName);
      }
      else if (step === StepPanelEnums.PanelTwo) {
        if (this.swShowOnlyOrderBreakdown) {
          this.filtersBreakdown = this.filtersBreakdown.filter(f => f.propertyName !== propertyName);
        } else {
          this.filtersQuotationDetail = this.filtersQuotationDetail.filter(f => f.propertyName !== propertyName);
        }
      }
      else if (step === StepPanelEnums.PanelThree) {
        this.filtersBreakdown = this.filtersBreakdown.filter(f => f.propertyName !== propertyName);
      }
    }
    else {
      let list: any[] = [];
      if (step === StepPanelEnums.PanelOne) {
        list = this.filtersQuotation;
      }
      else if (step === StepPanelEnums.PanelTwo) {
        list = this.swShowOnlyOrderBreakdown ? this.filtersBreakdown : this.filtersQuotationDetail;
      }
      else if (step === StepPanelEnums.PanelThree) {
        list = this.filtersBreakdown;
      }
      const existingFilter = list.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  get quotationLegend(): string {
    if (this.orderModel.orderId == 0)
      return 'Las cotizaciones resaltadas están aprobadas.';
    if (this.orderModel.quotationId != null)
      return 'La cotización resaltada es la asociada al pedido y está aprobada.';
    return 'Las cotizaciones resaltadas están aprobadas y son del mismo cliente y tipo del pedido.';
  }

  quotationRowClass = (item: QuotationSelectionTableModel): string =>
    this.getQuotationRestriction(item) ? '' : 'highlight-row';

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async selectionChange(event: { selectedItem?: any }) {
    const selectedId = event?.selectedItem?.id;
    if (!selectedId) {
      this.clearFutureStepSelections(this.currentStep);
      return;
    }
    if (this.swShowOnlyOrderBreakdown) {
      if (this.currentStep === StepPanelEnums.PanelOne) {
        await this.getQuotationById(selectedId);
      }
    } else {
      if (this.currentStep === StepPanelEnums.PanelOne) {
        await this.getQuotationById(selectedId);
      } else if (this.currentStep === StepPanelEnums.PanelTwo) {
        await this.getQuotationDetailById(selectedId);
      }
    }
  }

  get swSolutionCostCenterRule(): boolean {
    return this.orderModel.orderId != 0;
  }

  solutionRowClass = (item: QuotationDetailSelectionTableModel): string =>
    this.swSolutionCostCenterRule && item.costCenterId == this.orderModel.costCenterId ? 'highlight-row' : '';

  validateCurrentStepSelection(step: number): boolean {
    if (this.swShowOnlyOrderBreakdown) {
      if (step === StepPanelEnums.PanelTwo && !this.quotationId) {
        this.toastHandlerService.showToast(
          'warning',
          'Debe seleccionar una cotización válida antes de avanzar al despiece.',
        );
        return false;
      }
    } else {
      if (step === StepPanelEnums.PanelTwo && !this.quotationId) {
        this.toastHandlerService.showToast(
          'warning',
          'Debe seleccionar una cotización válida antes de avanzar a la solución.',
        );
        return false;
      } else if (step === StepPanelEnums.PanelThree && !this.quotationDetailId) {
        this.toastHandlerService.showToast(
          'warning',
          'Debe seleccionar una solución válida antes de avanzar al despiece.'
        );
        return false;
      }
    }
    return true;
  }
}
